import { classNames } from '@dimjs/utils';
import { flatbizDate } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { FC, Fragment, useMemo } from 'react';
import { GridItem } from '../../grid/grid-item';
import { CalendarProps, DayTagItem, TMonthDayItem } from '../types';

export type DayItemProps = {
  dayItem: TMonthDayItem;
  classPrefix: string;
  onClickDay: (selectedDate: string[]) => void;
  selectedList?: string[];
  selectedType: CalendarProps['selectedType'];
  onPreChange?: CalendarProps['onPreChange'];
  isFill?: CalendarProps['isFill'];
  selectedStartTag?: string;
  selectedEndTag?: string;
  dayTagMap?: Record<string, DayTagItem>;
  onDayCellRender?: CalendarProps['onDayCellRender'];
  onDisabledClick?: CalendarProps['onDisabledClick'];
};

export const DayItem: FC<DayItemProps> = (props) => {
  const dayItem = props.dayItem;
  const isShow = props.isFill ? dayItem.type !== 'empty' : !dayItem.type;
  const doNotOperate = !dayItem.inScope || dayItem.type === 'empty';

  const comparedData = useMemo(() => {
    if (!!dayItem.type) {
      return {};
    }
    if (!props.selectedList || props.selectedList.length === 0) {
      return {};
    }
    const startCompared = props.selectedList[0];
    const endCompared = props.selectedList[1];
    const target = dayItem.date;
    if (props.selectedType === 'one-select') {
      const target = dayItem.date;
      return { isStartSelected: startCompared ? startCompared === target : false };
    } else {
      return {
        isStartSelected: startCompared ? startCompared === target : false,
        isEndSelected: endCompared ? endCompared === target : false,
        isIn: startCompared && endCompared ? flatbizDate.in(target, startCompared, endCompared) : false,
      };
    }
  }, [dayItem.date, dayItem.type, props.selectedList, props.selectedType]);

  const dayTagInfo = useMemo(() => {
    if (!props.dayTagMap) return undefined;
    const current = props.dayTagMap[dayItem.date] || {};
    if (comparedData.isStartSelected) {
      return {
        ...current,
        topTag: props.selectedStartTag || current.topTag,
        label: current.label || dayItem.label,
        date: dayItem.date,
      };
    }
    if (comparedData.isEndSelected) {
      return {
        ...current,
        topTag: props.selectedEndTag || current.topTag,
        label: current.label || dayItem.label,
        date: dayItem.date,
      };
    }
    return {
      label: dayItem.label,
      ...current,
    };
  }, [
    comparedData.isEndSelected,
    comparedData.isStartSelected,
    dayItem.date,
    dayItem.label,
    props.dayTagMap,
    props.selectedEndTag,
    props.selectedStartTag,
  ]);

  const onClickDayHandle = hooks.useCallbackRef(async () => {
    if (props.onPreChange) {
      try {
        await props.onPreChange(dayItem);
      } catch (error) {
        return;
      }
    }
    if (doNotOperate) return;

    if (dayTagInfo?.disabled) {
      props.onDisabledClick?.(dayItem);
      return;
    }
    const currentDate = dayItem.date;
    const selectedStartDate = props.selectedList?.[0];
    const selectedEndDate = props.selectedList?.[1];
    if (props.selectedType === 'one-select') {
      props.onClickDay([currentDate]);
      return;
    }

    if (selectedStartDate && !selectedEndDate) {
      // 根据当前点击的区域，判断是开始时间还是结束时间
      if (flatbizDate.gt(currentDate, selectedStartDate)) {
        props.onClickDay([selectedStartDate, currentDate]);
        return;
      }
    }
    props.onClickDay([currentDate]);
  });

  const className = classNames(
    `${props.classPrefix}-day`,
    `${props.classPrefix}-${dayItem.week}`,
    {
      [`${props.classPrefix}-day-scope-out`]: !dayItem.inScope,
      [`${props.classPrefix}-day-empty`]: dayItem.type === 'empty',
      [`${props.classPrefix}-day-in`]: comparedData.isIn,
      [`${props.classPrefix}-day-start-selected`]: comparedData.isStartSelected,
      [`${props.classPrefix}-day-end-selected`]: comparedData.isEndSelected,
      [`${props.classPrefix}-day-fill-next`]: dayItem.type === 'next',
      [`${props.classPrefix}-day-fill-prev`]: dayItem.type === 'prev',
      [`${props.classPrefix}-day-disabled`]: !doNotOperate && dayTagInfo?.disabled,
    },
    dayTagInfo?.className,
  );

  return (
    <GridItem>
      <div className={className}>
        <div className={classNames(`${props.classPrefix}-day-content`)} onClick={onClickDayHandle}>
          {isShow ? (
            props.onDayCellRender ? (
              props.onDayCellRender({ ...dayItem, dayTagInfo })
            ) : (
              <Fragment>
                {dayTagInfo?.topTag ? (
                  <div
                    className={`${props.classPrefix}-day-toptag`}
                    style={dayTagInfo?.topTagColor ? { color: dayTagInfo?.topTagColor } : {}}
                  >
                    {dayTagInfo?.topTag}
                  </div>
                ) : null}
                <div
                  className={`${props.classPrefix}-day-label`}
                  style={dayTagInfo?.labelColor ? { color: dayTagInfo?.labelColor } : {}}
                >
                  {dayTagInfo?.label}
                </div>
                {dayTagInfo?.bottomTag ? (
                  <div
                    className={`${props.classPrefix}-day-bottomtag`}
                    style={dayTagInfo?.bottomTagColor ? { color: dayTagInfo?.bottomTagColor } : {}}
                  >
                    {dayTagInfo?.bottomTag}
                  </div>
                ) : null}
              </Fragment>
            )
          ) : null}
        </div>
      </div>
    </GridItem>
  );
};
